import { jitsiLocalStorage } from '@jitsi/js-utils';
import { sha256 } from 'js-sha256';
import moment from 'moment';

import { apiKEY, mtDomain } from '../base/config/constants';
import { browser } from '../base/lib-meet-hour';
import { fetchAxiosApi } from '../base/mtApi/functions';
import { showErrorNotification } from '../notifications';

import { SET_ONEDRIVE_AUTHORIZING, UPDATE_ONEDRIVE_TOKEN } from './actionTypes';
import { _authorizeOneDrive } from './functions';
import logger from './logger';

/**
 * Opens a centered popup for portal OneDrive auth.
 *
 * @param {string} url - The URL to open.
 * @returns {Window|null}
 */
function openCenteredPopup(url: string) {
    const width = 650;
    const height = 600;
    const left = window.screenX + ((window.outerWidth - width) / 2);
    const top = window.screenY + ((window.outerHeight - height) / 2.5);

    return window.open(url, 'onedrive_portal_auth', `width=${width},height=${height},left=${left},top=${top}`);
}

/**
 * Waits until popup is closed by the user.
 *
 * Uses `popup.closed` polling as the primary signal and focus/visibility
 * as a secondary fallback for restrictive browser policies.
 *
 * @param {Window} popup - Popup window handle.
 * @returns {Promise<void>}
 */
function waitForPopupClose(popup: Window): Promise<void> {
    return new Promise((resolve, reject) => {
        let didBlur = false;

        const resolveIfClosed = () => {
            try {
                if (!popup.closed) {
                    return false;
                }

                cleanup();
                resolve();

                return true;
            } catch (e) {
                return false;
            }
        };

        const handleBlur = () => {
            didBlur = true;
        };

        const handleReturnFromPopup = () => {
            // Primary path: popup is truly closed.
            if (resolveIfClosed()) {
                return;
            }

            // Fallback path for restrictive policies where `popup.closed`
            // cannot be read reliably.
            if (didBlur && document.visibilityState === 'visible') {
                cleanup();
                resolve();
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                handleReturnFromPopup();
            }
        };

        const pollClosed = window.setInterval(() => {
            resolveIfClosed();
        }, 500);

        const timeout = window.setTimeout(() => {
            cleanup();
            reject({ code: 'ONEDRIVE_OAUTH_TIMEOUT' });
        }, 180000);

        /**
         * cleanup function
         */
        function cleanup() {
            window.clearTimeout(timeout);
            window.clearInterval(pollClosed);
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('focus', handleReturnFromPopup);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        }

        // Keep a reference usage so lint does not treat the popup argument as unused.
        if (!popup) {
            cleanup();
            reject({ code: 'ONEDRIVE_POPUP_BLOCKED' });

            return;
        }

        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleReturnFromPopup);
        document.addEventListener('visibilitychange', handleVisibilityChange);
    });
}

/**
 * Reloads OneDrive token from recordsettings endpoint after portal auth.
 *
 * @param {Object} appConfig - App config.
 * @param {string} roomName - Meeting room identifier.
 * @param {?string} accessToken - Meet Hour access token.
 * @returns {Promise<Object>}
 */
async function refreshOneDriveTokenFromRecordSettings(
        appConfig: Object,
        roomName: ?string,
        accessToken?: string
) {
    const moement = moment().seconds(0)
        .milliseconds(0)
        .format('X');
    const credentials = sha256(`${apiKEY}:MH:${moement}`);

    const requestHeaders = {
        'Content-Type': 'application/json'
    };

    if (typeof accessToken === 'string' && accessToken.trim()) {
        requestHeaders.Authorization = `Bearer ${accessToken.trim()}`;
    }

    const recordSettings = await fetchAxiosApi('customer/recordsettings', {
        method: 'post',
        headers: requestHeaders,
        body: {
            client_id: appConfig.MH_CLIENT_ID,
            meeting_id: roomName,
            credentials,
            domain: mtDomain
        },
        config: {
            API_URL_PREFIX: appConfig.API_URL_PREFIX,
            API_VERSION_PREFIX: appConfig.API_VERSION_PREFIX
        }
    });

    const oneDriveTokenSource = recordSettings?.settings?.onedrive_access_token
        || recordSettings?.settings;
    const token = oneDriveTokenSource?.access_token;

    if (!token) {
        const error = new Error('ONEDRIVE_TOKEN_NOT_READY');

        error.code = 'ONEDRIVE_TOKEN_NOT_READY';
        error.details = 'OneDrive access token missing in recordsettings response after portal auth.';
        throw error;
    }

    return {
        token,
        rToken: oneDriveTokenSource?.refresh_token || '',
        expireDate: oneDriveTokenSource?.expires || oneDriveTokenSource?.expires_in || 0
    };
}

/**
 * Sleeps for a given duration.
 *
 * @param {number} ms - Delay in milliseconds.
 * @returns {Promise<void>}
 */
function sleep(ms: number): Promise<void> {
    return new Promise(resolve => {
        window.setTimeout(resolve, ms);
    });
}

/**
 * Retries recordsettings fetch until OneDrive token is available.
 *
 * @param {Object} appConfig - App config.
 * @param {?string} roomName - Meeting room identifier.
 * @param {?string} accessToken - Meet Hour access token.
 * @returns {Promise<Object>}
 */
async function waitForOneDriveTokenAfterPortalAuth(
        appConfig: Object,
        roomName: ?string,
        accessToken?: string
) {
    const maxAttempts = 30;
    const retryDelayMs = 2000;
    let lastError;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            return await refreshOneDriveTokenFromRecordSettings(appConfig, roomName, accessToken);
        } catch (error) {
            if (error?.code !== 'ONEDRIVE_TOKEN_NOT_READY') {
                throw error;
            }

            lastError = error;
            await sleep(retryDelayMs);
        }
    }

    const timeoutError = new Error('ONEDRIVE_TOKEN_EXCHANGE_FAILED');

    timeoutError.code = 'ONEDRIVE_TOKEN_EXCHANGE_FAILED';
    timeoutError.details = lastError?.details || 'OneDrive token was not ready after portal auth retry window.';
    throw timeoutError;
}

const ONE_DRIVE_NOTIFICATION_ERROR_MAP = {
    ONEDRIVE_INTEGRATION_NOT_CONFIGURED: 'recording.oneDriveIntegrationNotConfigured',
    ONEDRIVE_OAUTH_CANCELLED: 'recording.oneDriveOAuthCancelled',
    ONEDRIVE_MISSING_CLIENT_ID: 'recording.oneDriveMissingClientId',
    ONEDRIVE_WEB_CRYPTO_UNAVAILABLE: 'recording.oneDriveWebCryptoUnavailable',
    ONEDRIVE_POPUP_BLOCKED: 'recording.oneDrivePopupBlocked',
    ONEDRIVE_POPUP_CLOSED: 'recording.oneDrivePopupClosed',
    ONEDRIVE_OAUTH_TIMEOUT: 'recording.oneDriveOAuthTimedOut',
    ONEDRIVE_OAUTH_ERROR: 'recording.oneDriveOAuthFailed',
    ONEDRIVE_MISSING_AUTH_CODE: 'recording.oneDriveAuthCodeMissing',
    ONEDRIVE_TOKEN_EXCHANGE_FAILED: 'recording.oneDriveTokenExchangeFailed',
    ONEDRIVE_REFRESH_EXCHANGE_FAILED: 'recording.oneDriveRefreshTokenFailed',
    ONEDRIVE_TOKEN_INVALID: 'recording.oneDriveRefreshTokenFailed',
    ONEDRIVE_PROFILE_LOAD_FAILED: 'recording.oneDriveProfileLoadFailed',
    ONEDRIVE_DRIVE_USAGE_LOAD_FAILED: 'recording.oneDriveDriveUsageLoadFailed'
};

/**
 * Maps OneDrive auth/runtime errors to localized notification props.
 *
 * @param {Error|Object} error - The thrown error.
 * @returns {Object}
 */
function getOneDriveErrorNotification(error: Object = {}) {
    const errorCode = error?.code || error?.message;
    const descriptionKey = ONE_DRIVE_NOTIFICATION_ERROR_MAP[errorCode];
    const details = error?.details;

    if (descriptionKey) {
        const notification = {
            titleKey: 'recording.oneDriveError',
            descriptionKey
        };

        if (details) {
            notification.descriptionArguments = { details };
        }

        return notification;
    }

    return {
        titleKey: 'recording.oneDriveError',
        descriptionKey: 'recording.oneDriveAuthorizeFailed'
    };
}

/**
 * Resolves the OneDrive redirect URI from config.
 *
 * @param {Object} config - The app config.
 * @param {Object} locationURL - The current location URL object.
 * @returns {string}
 */
function resolveOneDriveRedirectUri(config: Object = {}, locationURL: Object) {
    const { onedrive = {} } = config;

    return onedrive.redirectURI
        || onedrive.redirectUri
        || onedrive.redirect_url
        || `${locationURL.origin}/static/oauth.html`;
}

/**
 * Builds OneDrive portal auth URL using config URL prefix.
 *
 * @param {Object} config - App config object.
 * @returns {string}
 */
function getOneDrivePortalAuthUrl(config: Object = {}) {
    const urlPrefix = typeof config.URL_PREFIX === 'string'
        ? config.URL_PREFIX.trim().replace(/\/+$/, '')
        : '';

    if (!urlPrefix) {
        return 'https://portal.meethour.io/customer/onedriveauth?showClosePage=1';
    }

    return `${urlPrefix}/customer/onedriveauth?showClosePage=1`;
}

/**
 * Resolves OneDrive auth strategy from config in a normalized form.
 *
 * @param {?string} authStrategy - Raw config auth strategy.
 * @returns {string}
 */
function resolveOneDriveAuthStrategy(authStrategy?: string) {
    if (typeof authStrategy !== 'string') {
        return '';
    }

    return authStrategy.trim().toLowerCase();
}

/**
 * Starts the OneDrive OAuth flow and stores resulting tokens.
 *
 * @returns {Function}
 */
export function authorizeOneDrive() {
    return (dispatch: Function, getState: Function) => {
        const state = getState();
        const { locationURL } = state['features/base/connection'];
        const config = state['features/base/config'] || {};
        const roomName = state['features/base/conference']?.room;
        const accessTokenFromRedux = state['features/base/jwt']?.accessToken;
        const accessTokenFromStorage = jitsiLocalStorage.getItem('accessToken');
        const meetHourAccessToken = accessTokenFromRedux || accessTokenFromStorage;
        const { onedrive = {} } = config;
        const authStrategy = resolveOneDriveAuthStrategy(onedrive.authStrategy);
        const clientId = onedrive.clientId;

        const hasClientId = typeof clientId === 'string' && clientId.length > 0;

        if (authStrategy === 'legacy-pkce-server') {
            dispatch({
                type: SET_ONEDRIVE_AUTHORIZING,
                authorizing: true
            });

            const popup = openCenteredPopup(getOneDrivePortalAuthUrl(config));

            if (!popup) {
                dispatch({
                    type: SET_ONEDRIVE_AUTHORIZING,
                    authorizing: false
                });
                dispatch(showErrorNotification(getOneDriveErrorNotification({
                    code: 'ONEDRIVE_POPUP_BLOCKED'
                })));

                return;
            }

            waitForPopupClose(popup)
                .then(() => waitForOneDriveTokenAfterPortalAuth(
                    config,
                    roomName,
                    typeof meetHourAccessToken === 'string' ? meetHourAccessToken : undefined
                ))
                .then(({ token, rToken, expireDate }) => {
                    dispatch(updateOneDriveToken(token, rToken, expireDate));
                })
                .catch(error => {
                    dispatch({
                        type: SET_ONEDRIVE_AUTHORIZING,
                        authorizing: false
                    });
                    logger.log('Cannot authorize oneDrive via portal flow', error);
                    dispatch(showErrorNotification(getOneDriveErrorNotification(error)));
                });

            return;
        }

        if (hasClientId) {
            let redirectURI = resolveOneDriveRedirectUri(config, locationURL);

            const isElectronApp = typeof browser !== 'undefined'
                && typeof browser.isElectron === 'function'
                && browser.isElectron();

            if (isElectronApp && typeof redirectURI === 'string') {
                const appUrlParam = `app_url=${encodeURIComponent('go.meethour.io://')}`;

                if (redirectURI.indexOf('app_url=') === -1) {
                    redirectURI = `${redirectURI}${redirectURI.includes('?') ? '&' : '?'}${appUrlParam}`;
                }
            }

            // Signal loading state so the UI shows a spinner during popup + server exchange.
            dispatch({
                type: SET_ONEDRIVE_AUTHORIZING,
                authorizing: true
            });

            _authorizeOneDrive(clientId, redirectURI, authStrategy)
                .then(({ token, rToken, expireDate }) => {
                    dispatch(updateOneDriveToken(token, rToken, expireDate));
                })
                .catch(error => {
                    dispatch({
                        type: SET_ONEDRIVE_AUTHORIZING,
                        authorizing: false
                    });
                    logger.log('Cannot authorize oneDrive', error);
                    dispatch(showErrorNotification(getOneDriveErrorNotification(error)));
                });

            return;
        }

        if (typeof clientId === 'string') {
            logger.error('Cannot authorize oneDrive: missing clientId in config.onedrive.clientId');
        } else {
            logger.error('Cannot authorize oneDrive: invalid clientId type in config');
        }

        dispatch(showErrorNotification({
            titleKey: 'recording.oneDriveError',
            descriptionKey: 'recording.oneDriveMissingClientId'
        }));

        return;
    };
}

/**
 * Creates an action for updating the OneDrive token state.
 *
 * @param {string} token - The access token.
 * @param {string} rToken - The refresh token.
 * @param {number} expireDate - Expiration timestamp.
 * @returns {Object}
 */
export function updateOneDriveToken(token: string, rToken: string, expireDate: number) {
    return {
        type: UPDATE_ONEDRIVE_TOKEN,
        token,
        rToken,
        expireDate
    };
}


